// Orchestrator: owns the simulation state, the selection and the frame loop,
// and wires the engine to the canvas, the on-map action bar, the panels and
// storage. A shift starts from the shift briefing (Lesson 1 or a Standard
// shift, #13) and ends with a debrief that adds to the saved career.

import {
  DEFAULT_WIND, MAX_INCIDENTS, PACES, ALERT_KINDS, WIRE_FACT, POWER_FACT,
} from './config/constants.js';
import {
  createSim, resetShift, openShift, step, delayPlan, reroutePlan, holdDrone, clearDrone, setSlotWind,
  previewAdjustment, windAt, cloneWind, seasonDay,
} from './core/sim.js';
import {
  stepOperations, previewTask, assignTask, holdOps, clearOps, returnToPad, addAircraft, dealWorkOrder,
} from './core/ops.js';
import {
  randomWind, randomWorkOrder, setTrafficLevel, FREE_MAX_AIRCRAFT,
} from './core/freeplay.js';
import { createFreePanel } from './ui/freePanel.js';
import { recordShift, startSeason, levelLabel } from './core/career.js';
import {
  scoreBreakdown, scoreRulesText, formatPoints, formatScore, trackPace,
} from './core/score.js';
import { createRenderer } from './ui/renderer.js';
import { createActionBar, SHORTCUTS } from './ui/actionbar.js';
import { attachRouteDrag } from './ui/dragroute.js';
import { createPlanList, createTrafficList, createWindBriefing, updateAlertRail, updateStats } from './ui/panels.js';
import { createOpsPanel } from './ui/opsPanel.js';
import { createAlertsPanel } from './ui/alertsPanel.js';
import { dispatchCrew, raiseAlert } from './core/alerts.js';
import { previewLanding, landCraftAt, divertToNearest, zonesSummary } from './core/zones.js';
import { createCoach, lessonOneSteps } from './ui/coach.js';
import {
  createBriefing, paceLabel, paceText, paceStep,
} from './ui/briefing.js';
import {
  loadWind, saveWind, loadBestScore, saveBestScore, loadCareer, saveCareer, loadSettings, saveSettings,
  loadFree, saveFree,
} from './services/storage.js';
import { $, setText, setHidden, setClass } from './ui/dom.js';
import { attachPlayOnPhone, maybeShowPhoneBanner } from './ui/phoneLinks.js';

const MAX_FRAME_DT = 0.05; // seconds; keeps a slow or backgrounded frame from teleporting drones
const REWARD_FLOAT_MS = 1600; // how long a "+15 near miss prevented" stays up
const OPS_TIP_MS = 2600; // how long a refused field assignment stays explained
const TIP_OFFSET = 16; // px from the pointer to the ops tip
const coarsePointer = () => window.matchMedia?.('(pointer: coarse)').matches ?? false;

export function startGame() {
  // the player's own wind briefing; Lesson 1 flies its own calm wind instead
  let userWind = cloneWind(loadWind() || DEFAULT_WIND);
  let career = loadCareer();
  let settings = loadSettings(); // speed of play, season length (#13)
  let freeSettings = loadFree(); // free play switches (#46)
  // the scope behind the first briefing: Lesson 1's farm, paused
  const sim = createSim({ wind: userWind, shift: { kind: 'orientation', aircraft: ['scout', 'duster'] } });
  let best = loadBestScore();
  // { kind: 'plan' | 'drone' | 'ops', id } or null. A plan keeps its id when
  // it launches, so a selected plan becomes the selected drone.
  let selection = null;
  let shiftDone = false; // this shift's debrief has been shown and recorded

  attachPlayOnPhone($('#play-on-phone'));
  maybeShowPhoneBanner($('#phone-banner'));

  const wrap = $('#scope-wrap');
  const renderer = createRenderer($('#scope'), wrap);
  const select = (kind, id) => { selection = id === null ? null : { kind, id }; };
  const selectedId = (kind) => (selection && selection.kind === kind ? selection.id : null);

  function act(action) {
    if (!selection || !sim.running) return;
    const { kind, id } = selection;
    if (action === 'emergency') { divertToNearest(sim, id); return; }
    if (kind === 'ops') {
      if (action === 'hold') holdOps(sim.ops, id);
      else if (action === 'clear') clearOps(sim.ops, id);
      else if (action === 'return') returnToPad(sim.ops, id);
      return;
    }
    if (kind === 'plan' && action === 'delay') delayPlan(sim, id);
    else if (kind === 'plan' && action === 'reroute') reroutePlan(sim, id);
    else if (kind === 'drone' && action === 'hold') holdDrone(sim, id);
    else if (kind === 'drone' && action === 'clear') clearDrone(sim, id);
  }

  const actionBar = createActionBar($('#action-bar'), {
    onAction: act,
    onClose: () => { selection = null; },
  });

  // side-list buttons act on their own row; nothing changes once a shift is over
  const live = (fn) => (id) => { if (sim.running) fn(sim, id); };
  const plans = createPlanList($('#plans'), $('#plan-count'), {
    onSelect: (id) => select('plan', id),
    onDelay: live(delayPlan),
    onReroute: live(reroutePlan),
  });

  const traffic = createTrafficList($('#strips'), $('#strip-count'), {
    onSelect: (id) => select('drone', id),
    onHold: live(holdDrone),
    onClear: live(clearDrone),
  });

  const ops = createOpsPanel($('#ops-list'), $('#ops-count'), {
    onSelect: (id) => select('ops', id),
  });

  const alertsPanel = createAlertsPanel($('#alerts-section'), $('#alert-list'), $('#alert-count'), {
    onDispatch: (id) => {
      if (!sim.running) return;
      const alert = sim.alerts.list.find((a) => a.id === id);
      // an emergency's button lands the craft at the nearest free pad (#33)
      if (alert?.kind === 'emergency') divertToNearest(sim, alert.craftId);
      else dispatchCrew(sim, id);
    },
  });

  const windBriefing = createWindBriefing(
    { slotsEl: $('#wind-slots'), nowEl: $('#wind-now'), limitsEl: $('#wind-limits') },
    sim,
    {
      onChange: (slot, change) => {
        if (sim.shiftKind === 'orientation') return; // Lesson 1's wind is fixed
        setSlotWind(sim, slot, change);
        // free play's wind is its own: the saved briefing is for Standard shifts
        if (sim.shiftKind === 'free') return;
        userWind = cloneWind(sim.wind);
        saveWind(userWind);
      },
    },
  );

  const coach = createCoach($('#coach'), $('#coach-text'));
  // Speed of play, in the header so it can change mid-shift (and in the briefing)
  const paceSelect = $('#pace-select');
  paceSelect.innerHTML = PACES.map((p) => `<option value="${p.value}">${paceLabel(p.value)} · ×${p.bonus}</option>`).join('');
  paceSelect.value = String(settings.pace);
  function changeSettings(next) {
    settings = next;
    saveSettings(settings);
    paceSelect.value = String(settings.pace);
    syncSpeedChoice();
  }

  // The debrief's Slower / Faster choice for the next shift (#13)
  const speedSteps = [...document.querySelectorAll('.speed-step')];
  function syncSpeedChoice() {
    setText($('#speed-now'), paceText(settings.pace));
    for (const b of speedSteps) b.disabled = paceStep(settings.pace, Number(b.dataset.step)) === null;
  }
  for (const b of speedSteps) {
    b.addEventListener('click', () => {
      const next = paceStep(settings.pace, Number(b.dataset.step));
      if (next !== null) changeSettings({ ...settings, pace: next });
    });
  }
  paceSelect.addEventListener('change', () => changeSettings({ ...settings, pace: Number(paceSelect.value) }));

  const shiftBriefing = createBriefing($('#briefing-veil'), {
    onStart: startShift,
    onNewSeason: () => {
      career = startSeason(career);
      saveCareer(career);
      return career;
    },
    onSettings: changeSettings,
  });

  const rail = $('#conflict-rail');
  const stats = {
    day: $('#stat-day'), active: $('#stat-active'), cleared: $('#stat-cleared'),
    adjusted: $('#stat-adjusted'), grounded: $('#stat-grounded'), incidents: $('#stat-incidents'),
    score: $('#stat-score'), best: $('#stat-best'), level: $('#stat-level'),
  };
  const workOrderStat = $('#stat-workorder');
  const rules = scoreRulesText();
  setText($('#score-rules'), rules);
  $('#score-chip').title = rules;
  const modal = $('#modal-veil');

  // Drag a plan's hub end to another hub to reroute it. Attached before the
  // click handler below so a drag can swallow its trailing click.
  const routeDrag = attachRouteDrag({
    canvas: $('#scope'),
    tip: $('#drag-tip'),
    renderer,
    sim,
    selectedPlanId: () => selectedId('plan'),
    onGrab: (id) => select('plan', id),
    onCommit: (id, hubIdx) => reroutePlan(sim, id, hubIdx),
    coarse: coarsePointer,
  });

  // With one of your aircraft selected, the field under the pointer is where
  // a click would send it: a ghost route and a verdict tip next to the pointer.
  const opsTip = $('#ops-tip');
  let hover = null; // { fieldIdx, pointer (canvas px) } while over a field
  let tipHoldUntil = 0; // a refused click keeps its explanation up for a moment
  const opsSelected = () => (selection?.kind === 'ops' && sim.ops ? selection.id : null);

  // Hovering only previews an aircraft that can take a field now (a busy
  // one's refusal shows when it's clicked instead).
  // A craft that can be sent to land at a pad (#33): one of yours, or a hub drone.
  const landableSelected = () => (selection && (selection.kind === 'ops' || selection.kind === 'drone') && sim.capacityEnabled ? selection.id : null);

  function opsPreviewAt(at) {
    if (!sim.running) return null;
    if (at.zoneId) {
      const id = landableSelected();
      return id === null ? null : previewLanding(sim, id, at.zoneId);
    }
    const id = opsSelected();
    if (id === null || at.fieldIdx === null) return null;
    const a = sim.ops.aircraft.find((x) => x.id === id);
    if (!a || (a.state !== 'parked' && a.state !== 'returning' && a.state !== 'padHold')) return null;
    return previewTask(sim.ops, id, at.fieldIdx, windAt(sim, sim.elapsed));
  }

  function showOpsTip(pv, pointer) {
    if (!pv || !pointer) { setHidden(opsTip, true); return; }
    const tone = !pv.ok ? 'bad' : (pv.note ? 'wind' : 'good');
    setClass(opsTip, `ops-tip ${tone}`);
    setText(opsTip, pv.ok ? `${pv.label}${pv.note ? ` · ${pv.note}` : ''}` : `${pv.label}: ${pv.reason}`);
    setHidden(opsTip, false);
    const size = renderer.size();
    const x = Math.min(pointer.x + TIP_OFFSET, size.w - opsTip.offsetWidth - 6);
    const y = Math.min(pointer.y + TIP_OFFSET, size.h - opsTip.offsetHeight - 6);
    opsTip.style.transform = `translate(${Math.round(Math.max(6, x))}px, ${Math.round(Math.max(6, y))}px)`;
  }

  $('#scope').addEventListener('pointermove', (e) => {
    const fieldIdx = renderer.fieldAt(e.clientX, e.clientY);
    const zoneId = sim.capacityEnabled ? renderer.zoneAt(e.clientX, e.clientY)?.id ?? null : null;
    hover = fieldIdx === null && zoneId === null ? null : { fieldIdx, zoneId, pointer: renderer.toLocal(e.clientX, e.clientY) };
  });
  $('#scope').addEventListener('pointerleave', () => { hover = null; });

  // Click/tap the scope: select what's under the pointer. Clicking the same
  // spot again cycles through overlapping items (plans leaving one hub).
  // With your aircraft selected, clicking a work-order field sends it there.
  $('#scope').addEventListener('click', (e) => {
    const hits = renderer.hitTest(e.clientX, e.clientY, sim, coarsePointer() ? 24 : 14);
    const fieldIdx = renderer.fieldAt(e.clientX, e.clientY);
    const opsId = opsSelected();
    // a pad (#33): land the selected craft there
    const zone = sim.capacityEnabled ? renderer.zoneAt(e.clientX, e.clientY) : null;
    const landId = landableSelected();
    if (zone && landId !== null && sim.running && !hits.some((h) => h.id === landId)) {
      const pv = landCraftAt(sim, landId, zone.id);
      if (pv && !pv.ok) { tipHoldUntil = performance.now() + OPS_TIP_MS; showOpsTip(pv, renderer.toLocal(e.clientX, e.clientY)); }
      if (pv) return;
    }
    if (opsId !== null && fieldIdx !== null && sim.running && !hits.some((h) => h.kind === 'ops')) {
      const pv = previewTask(sim.ops, opsId, fieldIdx, windAt(sim, sim.elapsed));
      if (pv) {
        if (pv.ok) assignTask(sim.ops, opsId, fieldIdx, windAt(sim, sim.elapsed));
        else { tipHoldUntil = performance.now() + OPS_TIP_MS; showOpsTip(pv, renderer.toLocal(e.clientX, e.clientY)); }
        return;
      }
    }
    if (!hits.length) { selection = null; return; }
    const at = selection ? hits.findIndex((h) => h.kind === selection.kind && h.id === selection.id) : -1;
    selection = hits[(at + 1) % hits.length];
  });

  // Free play controls (#46): each acts on the running session
  const freePanel = createFreePanel($('#free-panel'), {
    onSwitch: (change) => {
      freeSettings = { ...freeSettings, ...change };
      saveFree(freeSettings);
      if (sim.shiftKind !== 'free') return;
      if ('traffic' in change) setTrafficLevel(sim, change.traffic);
      if ('randomAlerts' in change) {
        sim.alerts.random = change.randomAlerts;
        sim.alerts.nextIn = Math.min(sim.alerts.nextIn, sim.alerts.interval * 0.5);
      }
      if ('randomWeather' in change) sim.free.randomWeather = change.randomWeather;
      if ('incidentsEnd' in change) sim.free.incidentsEnd = change.incidentsEnd;
    },
    onTrigger: (kind) => {
      const title = ALERT_KINDS[kind].title;
      if (raiseAlert(sim, kind)) return `${title}: triggered`;
      const why = {
        person: 'every field is already sprayed or has someone in it',
        birds: 'one flock at a time',
        tower: 'it needs a scouted field nobody is working',
        padBlocked: 'both hubs already have one',
        lowPower: 'both hubs already have one',
      }[kind];
      return `${title}: not now, ${why}`;
    },
    onAdd: (type) => {
      if (sim.ops.aircraft.length >= FREE_MAX_AIRCRAFT) return 'The hangar is full';
      const a = addAircraft(sim.ops, type);
      return `${a.call} is ready at ${a.padName}`;
    },
    onShuffle: () => {
      const next = randomWind(Math.random);
      for (const slot of Object.keys(next)) setSlotWind(sim, slot, next[slot]);
      windBriefing.load(sim);
      return 'New wind for every time of day';
    },
    onDeal: () => {
      dealWorkOrder(sim.ops, randomWorkOrder(Math.random));
      return `New work order: ${sim.ops.fields.map((f) => f.name).join(', ')}`;
    },
    onEnd: () => {
      selection = null;
      shiftBriefing.open(career, settings);
    },
  });

  // Pads in use at every landing zone, under the Operations panel (#33)
  const zonesRow = $('#zones-row');
  function updateZonesRow() {
    setHidden(zonesRow, !sim.capacityEnabled);
    if (!sim.capacityEnabled) return;
    const html = zonesSummary(sim).map(({ zone, used, blocked, pattern, full }) => {
      let t = blocked ? 'blocked' : `${used}/${zone.capacity}`;
      if (pattern) t += ` +${pattern} circling`;
      return `<span class="z${full ? ' full' : ''}" title="${zone.label}">${zone.short} ${t}</span>`;
    }).join('');
    if (zonesRow._html !== html) { zonesRow.innerHTML = html; zonesRow._html = html; }
  }

  // The next predicted conflict to deal with, soonest launch first; cycles.
  function selectNextWarning() {
    const queue = sim.plans
      .filter((p) => p.predicted && !p.grounded)
      .sort((a, b) => a.departAt - b.departAt || a.id - b.id);
    if (!queue.length) return;
    const at = queue.findIndex((p) => p.id === selectedId('plan'));
    select('plan', queue[(at + 1) % queue.length].id);
  }

  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey || !sim.running || shiftBriefing.isOpen()) return;
    if (e.target.closest?.('input, select, textarea, [contenteditable="true"], .share-veil')) return;
    const key = e.key.toLowerCase();
    if (key === 'escape') { if (selection) { selection = null; e.preventDefault(); } return; }
    if (key === 'n') { selectNextWarning(); e.preventDefault(); return; }
    if (SHORTCUTS[key] && selection) { act(SHORTCUTS[key]); e.preventDefault(); }
  });

  // Start a shift chosen in the briefing: a fresh farm, its wind, its lesson.
  function startShift({ kind, aircraft }) {
    // Lesson 1 is training: it doesn't use season days or depend on your level
    const standard = kind === 'standard';
    const isFree = kind === 'free';
    resetShift(sim, {
      // free play (#46) starts with random weather and a random work order
      wind: isFree ? randomWind(Math.random) : userWind,
      shift: {
        kind,
        aircraft,
        level: isFree ? (freeSettings.traffic === 'off' ? 'operator' : freeSettings.traffic) : career.level,
        dayStart: standard ? career.day : 1,
        seasonDays: standard ? settings.seasonDays : null,
        fields: isFree ? randomWorkOrder(Math.random) : undefined,
        free: isFree ? freeSettings : undefined,
      },
    });
    selection = null;
    hover = null;
    shiftDone = false;
    openShift(sim);
    windBriefing.load(sim);
    windBriefing.setLocked(kind === 'orientation');
    coach.start(kind === 'orientation' ? lessonOneSteps() : null);
    modal.hidden = true;
    last = performance.now();
  }

  $('#restart-btn').addEventListener('click', () => {
    modal.hidden = true;
    shiftBriefing.open(career, settings);
  });

  // End-of-shift card: score breakdown, and the saved best.
  const breakdownBody = $('#modal-breakdown');
  function fillBreakdown() {
    const { lines, total } = scoreBreakdown(sim);
    // free actions (0 points each) are explained in the rules line, not scored here
    breakdownBody.innerHTML = lines.filter((l) => l.each !== 0).map((l) => `
      <tr class="${l.key}">
        <th scope="row">${l.label}</th>
        <td class="n">${l.note ? l.note.replace('×', '&times;') : `${l.count} &times; ${formatPoints(l.each)}`}</td>
        <td class="pts">${formatPoints(l.points)}</td>
      </tr>`).join('');
    return total;
  }

  // What went well and what didn't, in the trainer's words.
  function debriefLines(completed) {
    const lines = [];
    const o = sim.ops;
    if (o) {
      lines.push(`${o.sprayed} of ${o.fields.length} fields sprayed, ${o.scouted} scouted`);
      if (!completed) {
        const left = o.fields.filter((f) => f.state !== 'sprayed').map((f) => f.name);
        if (left.length) lines.push(`Still to finish: ${left.join(', ')}`);
      }
    }
    lines.push(sim.incidents === 0
      ? 'No incidents: every aircraft kept its separation'
      : `${sim.incidents} separation ${sim.incidents > 1 ? 'incidents' : 'incident'}: an aircraft involved lands for inspection and its field starts over`);
    if (sim.hazardsHandled > 0 || sim.birdStrikes > 0) {
      const parts = [];
      if (sim.hazardsHandled) parts.push(`${sim.hazardsHandled} ${sim.hazardsHandled === 1 ? 'hazard' : 'hazards'} handled`);
      if (sim.birdStrikes) parts.push(`${sim.birdStrikes} bird ${sim.birdStrikes === 1 ? 'strike' : 'strikes'}`);
      lines.push(`Field and hub alerts: ${parts.join(', ')}`);
    }
    if (sim.separationLosses > 0) {
      const n = sim.separationLosses;
      lines.push(`Separation lost ${n} ${n === 1 ? 'time' : 'times'}: two aircraft came inside the ring. Hold or Delay earlier to keep them apart`);
    }
    if (sim.trafficEnabled) {
      lines.push(`${sim.nearMisses} near ${sim.nearMisses === 1 ? 'miss' : 'misses'} prevented, ${sim.cleared} hub ${sim.cleared === 1 ? 'flight' : 'flights'} landed`);
    }
    return lines;
  }

  function showDebrief() {
    const total = fillBreakdown();
    const kind = sim.shiftKind;
    const completed = Boolean(sim.ops?.complete);
    const outOfIncidents = sim.incidents >= MAX_INCIDENTS;

    let title = 'Shift ended';
    if (completed) title = kind === 'orientation' ? 'Lesson 1 complete' : 'Work order complete';
    else if (sim.seasonOver) title = 'Season complete';
    if (kind === 'free') title = 'Free play ended';
    setText($('#modal-title'), title);
    $('#modal-veil .modal').classList.toggle('done', completed || sim.seasonOver);
    let text = 'Every field on the work order was scouted and sprayed.';
    if (outOfIncidents) text = 'Three separation incidents this shift. Ground crews are re-checking the airspace before anyone flies again.';
    else if (sim.seasonOver && !completed) text = `The season's last day (day ${sim.seasonDays}) is over.`;
    setText($('#modal-text'), text);
    $('#modal-debrief').innerHTML = debriefLines(completed).map((l) => `<li>${l}</li>`).join('');
    // field notes: the real-world side of the alerts this shift ran into (#31)
    const extraNotes = { wire: WIRE_FACT, power: POWER_FACT };
    const notes = [...(sim.alerts?.notes ?? [])].map((k) => extraNotes[k] ?? ALERT_KINDS[k]?.fact).filter(Boolean);
    setHidden($('#modal-notes'), notes.length === 0);
    $('#modal-notes-list').innerHTML = notes.map((n) => `<li>${n}</li>`).join('');

    // the best score compares Standard shifts only: Lesson 1 is always the same farm
    const counts = kind === 'standard';
    const firstScore = best === null;
    const isNewBest = counts && (firstScore || total > best);
    if (isNewBest) { best = total; saveBestScore(total); }
    setText($('#modal-score'), formatScore(total));
    setHidden($('#modal-new-best'), !isNewBest || firstScore);
    if (!counts) setText($('#modal-best'), '');
    else if (firstScore) setText($('#modal-best'), 'First score saved as your best');
    else setText($('#modal-best'), isNewBest ? '' : `Best ${formatScore(best)}`);
    const first = sim.dayStart;
    const last = seasonDay(sim);
    setText($('#modal-day'), kind !== 'standard' ? 'Training' : (first === last ? `${first}` : `${first}–${last}`));
    setText($('#modal-grounded'), sim.groundedTotal);

    // free play (#46) doesn't count toward the career
    const result = kind === 'free'
      ? { career, unlocked: [], outcome: null }
      : recordShift(career, {
        kind, total, completed, incidents: sim.incidents, daysUsed: sim.day,
      });
    const earned = result.career.points - career.points;
    career = result.career;
    saveCareer(career);
    let line = kind === 'free'
      ? 'Free play doesn\'t count toward your career'
      : `+${earned} career points · ${career.points} in total`;
    if (result.unlocked.length) line += ` · Unlocked: ${result.unlocked.join(', ')}`;
    setText($('#modal-career'), line);
    $('#modal-career').classList.toggle('unlocked', result.unlocked.length > 0);

    // the level decision (Standard shifts only)
    const levelEl = $('#modal-level');
    const o = result.outcome;
    setHidden(levelEl, !o);
    if (o) {
      const headline = {
        promote: `Promoted to ${levelLabel(o.level)}`,
        stay: `Staying at ${levelLabel(o.level)}`,
        demote: `Moved back to ${levelLabel(o.level)}`,
      }[o.change];
      levelEl.innerHTML = `<b>${headline}</b>: ${o.reason}.`;
      setClass(levelEl, `level-line ${o.change}`);
    }

    syncSpeedChoice();
    selection = null;
    modal.hidden = false;
    $('#restart-btn').focus();
  }

  function syncModal() {
    if (!sim.running && !shiftDone) {
      shiftDone = true;
      showDebrief();
    }
  }

  // Keep the selection pointing at something that still exists: a launched
  // plan becomes its drone; a landed or crashed drone clears it.
  function settleSelection() {
    if (!selection) return null;
    if (selection.kind === 'ops') {
      const a = sim.ops?.aircraft.find((x) => x.id === selection.id);
      if (!a) selection = null;
      return a || null;
    }
    if (selection.kind === 'plan') {
      const p = sim.plans.find((x) => x.id === selection.id);
      if (p) return p;
      selection = { kind: 'drone', id: selection.id };
    }
    const d = sim.drones.find((x) => x.id === selection.id);
    if (!d) selection = null;
    return d || null;
  }

  // Rewards (near misses prevented): a floating "+15" where the flights would
  // have met, and a flash on the Score chip.
  const scoreChip = $('#score-chip');
  function showRewards() {
    for (const r of sim.rewards.splice(0)) {
      const at = renderer.toPx(r);
      const el = document.createElement('div');
      el.className = 'reward-float';
      el.innerHTML = `<b>${formatPoints(r.points)}</b> ${r.label || 'near miss prevented'}`;
      el.style.left = `${Math.round(at.x)}px`;
      el.style.top = `${Math.round(at.y)}px`;
      wrap.appendChild(el);
      setTimeout(() => el.remove(), REWARD_FLOAT_MS);
      scoreChip.classList.remove('flash');
      void scoreChip.offsetWidth; // restart the flash animation
      scoreChip.classList.add('flash');
    }
  }

  // Ghost route on the scope: the route being dragged, else the selected
  // plan's Reroute (faint; bright while its button is hovered or focused).
  function overlayFor(dragState, previews) {
    if (dragState) return { drag: dragState, ghost: dragState.preview ? { preview: dragState.preview, strong: true } : null };
    if (previews.reroute) return { ghost: { preview: previews.reroute, strong: actionBar.previewing() === 'reroute' } };
    return {};
  }

  let last = performance.now();
  function frame(now) {
    const dt = Math.min(MAX_FRAME_DT, (now - last) / 1000);
    last = now;
    // the briefing pauses the farm behind it
    // the briefing pauses the farm behind it; speed of play scales the game
    // clock, in steps no longer than a frame so fast-forward stays accurate
    if (!shiftBriefing.isOpen()) {
      let left = dt * settings.pace;
      while (left > 1e-9 && sim.running) {
        const h = Math.min(MAX_FRAME_DT, left);
        step(sim, h);
        stepOperations(sim, h, windAt(sim, sim.elapsed));
        trackPace(sim, h, settings.pace);
        left -= h;
      }
    }
    const target = settleSelection();
    const planId = selectedId('plan');
    const previews = planId === null ? {} : {
      delay: previewAdjustment(sim, planId, 'delay'),
      reroute: previewAdjustment(sim, planId, 'reroute'),
    };

    const overlay = overlayFor(routeDrag.frame(), previews);
    const opsPreview = hover ? opsPreviewAt(hover) : null;
    if (opsPreview) overlay.opsPreview = opsPreview;
    if (now > tipHoldUntil) showOpsTip(opsPreview, hover?.pointer);
    const coachAt = coach.update(sim, selection);
    if (coachAt) overlay.coach = coachAt;
    renderer.draw(sim, selection, overlay);
    // keep the bar off the selected flight's route and its reroute preview
    const avoid = !target ? [] : [target.from, target.to, previews.reroute?.from, previews.reroute?.to]
      .filter(Boolean).map(renderer.toPx);
    actionBar.update(
      target, selection?.kind, renderer.anchorOf(sim, selection), sim.elapsed, renderer.size(), previews, avoid,
    );
    showRewards();
    ops.update(sim, selectedId('ops'));
    alertsPanel.update(sim);
    updateZonesRow();
    freePanel.sync(sim);
    plans.update(sim, selectedId('plan'));
    traffic.update(sim, selectedId('drone'));
    windBriefing.update(sim);
    updateAlertRail(rail, sim);
    updateStats(stats, sim, best);
    if (sim.shiftKind === 'standard') {
      setText(stats.day, `${seasonDay(sim)} / ${sim.seasonDays}`);
      setText(stats.level, levelLabel(sim.level));
    } else if (sim.shiftKind === 'free') {
      setText(stats.day, `Day ${sim.day}`);
      setText(stats.level, 'Free play');
    } else {
      setText(stats.day, sim.shiftKind === 'orientation' ? 'Lesson' : String(sim.day));
      setText(stats.level, 'Training');
    }
    setText(workOrderStat, sim.ops ? `${sim.ops.sprayed} / ${sim.ops.fields.length}` : '—');
    syncModal();
    requestAnimationFrame(frame);
  }
  windBriefing.load(sim);
  windBriefing.setLocked(true);
  shiftBriefing.open(career, settings);
  requestAnimationFrame(frame);

  // test/debug hook for browser checks (a read-only peek at the shift); only
  // in dev or with ?debug, like the phone version's, so it doesn't ship to players
  if (import.meta.env.DEV || new URLSearchParams(window.location.search).has('debug')) {
    window.__farmatc = { get sim() { return sim; }, raise: (kind, place) => raiseAlert(sim, kind, place) };
  }
}
